import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './TeamGallery.module.css';

const TeamGallery = () => {
  const { taskId, teamName } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchGallery = async () => {
      try {
        const res = await axios.get(
          `/api/tasks/${taskId}/gallery/${encodeURIComponent(teamName)}`
        );
        if (cancelled) return;
        setData(res.data);
        setError(null);
        setFirstLoad(false);
        // Keep polling while the composites are still being generated.
        if (res.data.processing) {
          pollRef.current = setTimeout(fetchGallery, 4000);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading gallery:', err);
        setError('Something went wrong loading your gallery. Please refresh.');
        setFirstLoad(false);
      }
    };

    fetchGallery();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [taskId, teamName]);

  const total = data ? data.edited.length : 0;
  const closeLightbox = () => setLightboxIndex(null);
  const showPrev = () => setLightboxIndex((i) => (i + total - 1) % total);
  const showNext = () => setLightboxIndex((i) => (i + 1) % total);

  // Keyboard controls while the lightbox is open.
  useEffect(() => {
    if (lightboxIndex === null || total === 0) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      else if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i + total - 1) % total);
      else if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % total);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, total]);

  const displayName = (data && data.team) || decodeURIComponent(teamName);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="https://www.wejam.studio" target="_blank" rel="noopener noreferrer">
          <img src="/logo.png" alt="WeJam Studio" className={styles.logo} />
        </a>
      </header>

      <div className={styles.hero}>
        <p className={styles.kicker}>Album Cover Challenge</p>
        <h1 className={styles.title}>{displayName}</h1>
        <p className={styles.subtitle}>Your team’s cover art, ready to share 🤘</p>
      </div>

      {firstLoad && <p className={styles.status}>Loading your gallery…</p>}

      {error && <p className={styles.status}>{error}</p>}

      {!firstLoad && !error && data && (
        <>
          {data.edited.length === 0 && data.processing && (
            <div className={styles.status}>
              <div className={styles.spinner} />
              <p>Creating your album covers… this takes a few seconds. Hang tight!</p>
            </div>
          )}

          {data.edited.length === 0 && !data.processing && (
            <p className={styles.status}>
              {data.found
                ? 'Your covers aren’t ready yet — check back shortly.'
                : 'We couldn’t find any covers for this team yet.'}
            </p>
          )}

          {data.edited.length > 0 && (
            <div className={styles.grid}>
              {data.edited.map((url, i) => (
                <figure key={url} className={styles.card}>
                  <button type="button" className={styles.imageButton} onClick={() => setLightboxIndex(i)}>
                    <img src={url} alt={`Album cover ${i + 1}`} className={styles.image} loading="lazy" />
                  </button>
                  <figcaption className={styles.caption}>
                    <span>#{i + 1}</span>
                    <a href={url} download className={styles.download}>Download</a>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </>
      )}

      <footer className={styles.footer}>Made with WeJam Studio</footer>

      {lightboxIndex !== null && data && data.edited[lightboxIndex] && (
        <div className={styles.lightbox} onClick={closeLightbox}>
          <button className={styles.lbClose} onClick={closeLightbox} aria-label="Close">×</button>
          {total > 1 && (
            <button
              className={`${styles.lbNav} ${styles.lbPrev}`}
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          <img
            src={data.edited[lightboxIndex]}
            alt={`Album cover ${lightboxIndex + 1}`}
            className={styles.lbImage}
            onClick={(e) => e.stopPropagation()}
          />
          {total > 1 && (
            <button
              className={`${styles.lbNav} ${styles.lbNext}`}
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="Next"
            >
              ›
            </button>
          )}
          <a
            href={data.edited[lightboxIndex]}
            download
            className={styles.lbDownload}
            onClick={(e) => e.stopPropagation()}
          >
            Download
          </a>
        </div>
      )}
    </div>
  );
};

export default TeamGallery;
